// Cliente HTTP para a API do Aura Nota (autenticacao + consentimento + documentos + auditoria).

const TOKEN_KEY = 'auranota_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Org ativa conhecida pelo cliente (issue #330/#482): a unica org que o
// frontend consegue identificar e a de impersonation — fica no claim
// `impersonating_org_id` do JWT. Usuarios comuns nao tem seletor de org;
// para eles o backend resolve a primeira membership (e agora valida a
// role NA org resolvida — fix auditoria S14).
export function getActiveOrgId(): string | null {
  const token = getToken();
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const payload = JSON.parse(
      atob(part.replace(/-/g, '+').replace(/_/g, '/')),
    ) as Record<string, unknown>;
    return typeof payload.impersonating_org_id === 'string'
      ? payload.impersonating_org_id
      : null;
  } catch {
    return null;
  }
}

// Header de binding de org ativa (issue #326): X-Organization-ID e opcional —
// quando omitido o backend resolve pela membership/impersonation do JWT.
// Fix auditoria S14 (HIGH-1): endpoints org-scoped (certificados, auditoria)
// enviam a org ativa para que a role seja validada NA org resolvida.
function orgBoundHeaders(orgId?: string | null): Record<string, string> {
  const headers = authHeaders();
  if (orgId) headers['X-Organization-ID'] = orgId;
  return headers;
}

// Converte qualquer valor de `detail` (string, objeto, array) em uma mensagem
// legivel. Previne o bug [object Object] quando new Error() recebe um objeto.
function extractDetail(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    // Objeto com .message (ex: DuplicateInfo sem status=duplicate)
    if ('message' in detail && typeof (detail as Record<string, unknown>).message === 'string') {
      return (detail as Record<string, unknown>).message as string;
    }
    // Objeto com .msg (ex: itens de erro de validacao do FastAPI)
    if ('msg' in detail && typeof (detail as Record<string, unknown>).msg === 'string') {
      return (detail as Record<string, unknown>).msg as string;
    }
    // Array de erros de validacao (422) — junta mensagens
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((item) => extractDetail(item, ''))
        .filter(Boolean);
      return msgs.length > 0 ? msgs.join('; ') : fallback;
    }
    // Outro objeto — serializa para JSON legivel
    try {
      return JSON.stringify(detail);
    } catch {
      return fallback;
    }
  }
  return fallback;
}

// Erro tipado generico lancado por handle(): carrega o status HTTP para que
// callers distingam falha de credencial (401/403) de erro transitorio (5xx,
// corpo invalido). Issue #558 — sem o status, o restore de sessao so podia
// tratar toda falha como sessao expirada (catch-all -> clearToken()).
// Mesmo padrao de CertificateApiError; estende Error -> retro-compativel
// com todos os catch/instanceof Error existentes.
export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// Intercepta erros globais de auth/plano antes de lancar a excecao:
// - 401 (FIX #215 / Issue #261 / #416): sessao expirada ou token invalido —
//   limpa o token persistido e dispara evento global para que o Layout
//   mostre mensagem amigavel (i18n) e redirecione ao login. Nao usamos
//   window.location.href (redirect duro) porque o app e um SPA de
//   view-state: o pathname e sempre '/' e o redirect nunca disparava.
//   So dispara se ha token no localStorage e NAO estamos em path publico
//   (/invite, /login, /signup) — senao causa redirect/banner em loop.
// - 402 (Issue #248): Payment Required — o Layout escuta o evento e mostra
//   um banner com link para /billing?plan=pro.
function interceptGlobalErrors(res: Response, detail: string): void {
  if (res.status === 401) {
    const token = getToken();
    const publicPaths = ['/invite', '/login', '/signup'];
    const isPublicPath = publicPaths.includes(window.location.pathname);
    if (token && !isPublicPath) {
      clearToken();
      window.dispatchEvent(
        new CustomEvent('auranota:session-expired', {
          detail: { expired: true, message: detail },
        }),
      );
    }
  }
  if (res.status === 402) {
    window.dispatchEvent(
      new CustomEvent('auranota:upgrade-required', { detail: { message: detail } }),
    );
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = extractDetail(body.detail, detail);
    } catch {
      // corpo nao-JSON; mantem o status
    }
    interceptGlobalErrors(res, detail);
    // Issue #558: ApiError (em vez de Error puro) preserva o status HTTP —
    // callers podem distinguir 401/403 (credencial invalida) de 5xx/transitorio.
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export interface UserMe {
  id: string;
  email: string;
  // Nome de exibicao do usuario (editavel em "Minha Conta").
  name: string | null;
  is_active: boolean;
  role: string;
  preferred_language: string | null;
  // Issue #330: contexto de impersonation (super_user "acessar como" tenant).
  impersonating_org_id: string | null;
  impersonating_org_name: string | null;
}

export interface ConsentRecord {
  id: number;
  consent_type: string;
  consent_version: string;
  accepted: boolean;
  ip_address: string | null;
  user_agent: string | null;
  accepted_at: string | null;
}

export interface ConsentCheck {
  needs_consent: boolean;
  current_version: string;
  last_accepted: string | null;
  accepted_types: string[];
}

export async function login(email: string, password: string): Promise<string> {
  const form = new URLSearchParams();
  form.set('username', email);
  form.set('password', password);
  const res = await fetch('/api/auth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: form.toString(),
  });
  const data = await handle<{ access_token: string; token_type: string }>(res);
  setToken(data.access_token);
  return data.access_token;
}

export async function fetchMe(): Promise<UserMe> {
  const res = await fetch('/api/auth/me', { headers: authHeaders() });
  return handle<UserMe>(res);
}

// Bug #2: persiste o idioma preferido do usuario no backend.
export async function updatePreferredLanguage(
  language: string,
): Promise<UserMe> {
  const res = await fetch('/api/auth/me', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ preferred_language: language }),
  });
  return handle<UserMe>(res);
}

// Edicao de dados pessoais ("Meus dados" em Minha Conta): PATCH parcial —
// campos ausentes nao alteram o valor atual. ``name`` vazio/whitespace
// retorna 422; ``preferred_language`` invalido retorna 400.
export interface UpdateProfilePayload {
  name?: string;
  preferred_language?: string;
}

export async function updateProfile(payload: UpdateProfilePayload): Promise<UserMe> {
  const res = await fetch('/api/auth/me', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload),
  });
  return handle<UserMe>(res);
}

// Troca de senha da propria conta ("Minha Conta" > "Alterar senha").
// Senha atual errada -> 400 generico; nova senha fraca -> 422.
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<{ message: string }> {
  const res = await fetch('/api/auth/change-password', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });
  return handle<{ message: string }>(res);
}

export async function checkConsent(): Promise<ConsentCheck> {
  const res = await fetch('/api/auth/consent/check', { headers: authHeaders() });
  return handle<ConsentCheck>(res);
}

export async function recordConsent(
  consentType: string,
  consentVersion: string,
): Promise<ConsentRecord> {
  const res = await fetch('/api/auth/consent', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ consent_type: consentType, consent_version: consentVersion }),
  });
  return handle<ConsentRecord>(res);
}

export async function consentHistory(): Promise<ConsentRecord[]> {
  const res = await fetch('/api/auth/consent/history', { headers: authHeaders() });
  return handle<ConsentRecord[]>(res);
}

export const LEGAL_VERSION = '1.0';
export const CONSENT_TYPES = ['terms', 'privacy', 'cookies'] as const;

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

export interface ContainerInfo {
  container_type: string;
  container_name: string;
  attachment_name: string | null;
}

export interface Provenance {
  container_chain: ContainerInfo[];
  source_path: string | null;
  file_type: string | null;
  file_name: string | null;
  extraction_method: string | null;
  inherited_from: { child_source_type: string; child_file_name: string | null } | null;
  page: number | null;
  zone: string | null;
  template: string | null;
}

export interface DocumentSummary {
  id: string;
  import_id: string | null;
  filename: string;
  content_type: string;
  status: string;
  doc_type: string;
  source_type: string;
  avg_confidence: number;
  fields_count: number;
  needs_review: boolean;
  review_reason: string | null;
  has_children: boolean;
  children_count: number;
  total_documents: number;
  total_errors: number;
  provenance: Provenance | null;
  source_path: string | null;
  processing_steps: string[];
  created_at: string | null;
  updated_at: string | null;
  // Issue #341: data/hora de processamento.
  processing_started_at: string | null;
  processed_at: string | null;
  deleted_at: string | null;
  deleted_by: string | null;
  uploaded_by: string | null;
}

export interface DocumentListResponse {
  documents: DocumentSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface ExtractedFieldData {
  value: unknown;
  confidence: number;
  source: string | null;
  raw: string | null;
  corrected?: boolean;
  provenance?: Provenance | null;
}

export interface DocumentResultData {
  file: string;
  document_type: string;
  source_type: string;
  fields: Record<string, ExtractedFieldData>;
  needs_review: boolean;
  review_reason: string | null;
  children: DocumentResultData[];
  error: string | null;
  processing_time_ms: number;
  metadata: Record<string, unknown>;
  processing_steps?: string[];
}

export interface PipelineResultData {
  root: DocumentResultData;
  total_documents: number;
  total_extracted: number;
  total_needs_review: number;
  total_errors: number;
}

export interface DocumentDetailResponse {
  document: DocumentSummary;
  pipeline_result: PipelineResultData;
  lifecycle_events: LifecycleEvent[];
}

export interface LifecycleEvent {
  id: string;
  event: string;
  user_id: string | null;
  justification: string | null;
  created_at: string | null;
}

// Issue #276: quando o processamento e assincrono (202 Accepted), o backend
// retorna um placeholder em vez de um PipelineResultData completo:
//   { "status": "pendente", "async": true }
// O frontend detecta a ausencia de `root` e inicia polling de status.
export interface AsyncResultPlaceholder {
  status: string;
  async: boolean;
}

export interface UploadResponse {
  documents: DocumentSummary[];
  results: (PipelineResultData | AsyncResultPlaceholder)[];
  duplicates: boolean[];
}

// Issue #219: Informacoes sobre duplicata detectada (resposta 409).
export interface DuplicateInfo {
  status: 'duplicate' | 'batch_duplicates';
  existing_import_id: string;
  existing_filename: string;
  existing_created_at: string | null;
  duplicate_type: 'file' | 'nf';
  message: string;
  // Novos campos para batch (status === 'batch_duplicates'):
  duplicates?: BatchDuplicate[];
  non_duplicate_indices?: number[];
}

// Issue #219: Duplicata individual dentro de um batch.
export interface BatchDuplicate {
  batch_index: number;
  uploaded_filename: string;
  existing_import_id: string;
  existing_filename: string;
  existing_created_at: string | null;
  duplicate_type: 'file' | 'nf';
  message: string;
}

// Issue #219: Erro de duplicada lancado quando o backend retorna 409.
export class DuplicateError extends Error {
  info: DuplicateInfo;
  constructor(info: DuplicateInfo) {
    super(info.message || 'Documento duplicado');
    this.name = 'DuplicateError';
    this.info = info;
  }
}

export interface DocumentListParams {
  doc_type?: string;
  status?: string;
  import_id?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
  include_deleted?: boolean;
  // Issue #329: filtro por cliente do contador (separacao de telas).
  cliente_id?: string;
  // Issue #418: exportar apenas documentos selecionados.
  ids?: string[];
}

// Issue #219: Upload com deteccao de duplicata (409 Conflict).
// Retorna UploadResponse em caso de sucesso, ou lanc,a DuplicateError em caso de 409.
// modes: array de modes por arquivo (ex: ["replace","none","archive"]) para
// re-envio apos decisao do usuario no modal de batch duplicates.
export async function uploadDocument(
  files: File | File[],
  onProgress?: (percent: number) => void,
  mode?: 'replace' | 'archive' | 'duplicate',
  modes?: string[],
  onUploaded?: () => void,
  clienteId?: string | null,
): Promise<UploadResponse> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    const fileArray = Array.isArray(files) ? files : [files];
    fileArray.forEach((f) => formData.append('files', f));

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) {
        const pct = Math.round((e.loaded / e.total) * 100);
        onProgress(pct);
        // Issue #267: fallback — se o evento upload.load nao disparar
        // (proxy/browser edge), 100% de progresso tambem sinaliza que o
        // corpo foi enviado e o backend esta processando. Idempotente:
        // o caller ja trata transicoes repetidas para 'processing'.
        if (pct >= 100 && onUploaded) onUploaded();
      }
    });

    // Issue #268: quando o upload do corpo da request termina (100%), o
    // backend inicia o processamento sincrono. Avisa o caller para que a UI
    // possa transicionar de ENVIANDO -> PROCESSANDO enquanto aguarda a
    // resposta final.
    xhr.upload.addEventListener('load', () => {
      if (onUploaded) onUploaded();
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (err) {
          reject(new Error('Resposta invalida do servidor.'));
        }
      } else if (xhr.status === 409) {
        // Issue #219: Duplicata detectada — parseia DuplicateInfo.
        // Suporta tanto status='duplicate' (1 arquivo) quanto
        // status='batch_duplicates' (lista de duplicatas do lote).
        try {
          const body = JSON.parse(xhr.responseText);
          const detail = body?.detail;
          if (detail && (detail.status === 'duplicate' || detail.status === 'batch_duplicates')) {
            reject(new DuplicateError(detail as DuplicateInfo));
          } else {
            reject(new Error(extractDetail(detail, 'Documento duplicado')));
          }
        } catch {
          reject(new Error('Documento duplicado'));
        }
      } else {
        try {
          const body = JSON.parse(xhr.responseText);
          reject(new Error(extractDetail(body?.detail, `${xhr.status} ${xhr.statusText}`)));
        } catch {
          reject(new Error(`${xhr.status} ${xhr.statusText}`));
        }
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Erro de rede no upload.')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelado.')));

    // Issue #219: adiciona ?mode=... ou ?modes=[...] quando fornecido.
    // modes (array por arquivo) tem prioridade sobre mode (singular).
    let url = '/api/documents/upload';
    const params: string[] = [];
    if (clienteId) params.push(`cliente_id=${encodeURIComponent(clienteId)}`);
    if (modes) {
      params.push(`modes=${encodeURIComponent(JSON.stringify(modes))}`);
    } else if (mode) {
      params.push(`mode=${mode}`);
    }
    if (params.length) url += '?' + params.join('&');
    xhr.open('POST', url);
    const token = getToken();
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.send(formData);
  });
}

export async function listDocuments(params?: DocumentListParams): Promise<DocumentListResponse> {
  const search = new URLSearchParams();
  if (params?.doc_type) search.set('doc_type', params.doc_type);
  if (params?.status) search.set('status', params.status);
  if (params?.import_id) search.set('import_id', params.import_id);
  if (params?.date_from) search.set('date_from', params.date_from);
  if (params?.date_to) search.set('date_to', params.date_to);
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.offset) search.set('offset', String(params.offset));
  if (params?.include_deleted) search.set('include_deleted', 'true');
  if (params?.cliente_id) search.set('cliente_id', params.cliente_id);
  if (params?.ids && params.ids.length > 0) search.set('ids', params.ids.join(','));

  const qs = search.toString();
  const url = `/api/documents${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: authHeaders() });
  return handle<DocumentListResponse>(res);
}

export async function getDocument(docId: string): Promise<DocumentDetailResponse> {
  const res = await fetch(`/api/documents/${docId}`, { headers: authHeaders() });
  return handle<DocumentDetailResponse>(res);
}

// Issue #279: Status do lote (batch) ativo do usuario.
export interface BatchStatus {
  active: boolean;
  total?: number;
  concluido?: number;
  erro?: number;
  processando?: number;
  pendente?: number;
}

export async function getBatchStatus(): Promise<BatchStatus> {
  const res = await fetch('/api/documents/batch-status', { headers: authHeaders() });
  return handle<BatchStatus>(res);
}

export async function getDocumentAudit(docId: string): Promise<{
  document_id: string;
  filename: string;
  lifecycle_events: LifecycleEvent[];
  audit_entries: AuditEntry[];
}> {
  const res = await fetch(`/api/documents/${docId}/audit`, { headers: authHeaders() });
  return handle(res);
}

export async function deleteDocument(
  docId: string,
): Promise<{ success: boolean; message: string; document_id: string; deleted_at: string | null }> {
  const res = await fetch(`/api/documents/${docId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return handle(res);
}

export async function restoreDocument(
  docId: string,
): Promise<{ success: boolean; message: string; document: DocumentSummary }> {
  const res = await fetch(`/api/documents/${docId}/restore`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return handle(res);
}

// ---------------------------------------------------------------------------
// Issue #182: Export de lista + download de documento + edicao de campos
// ---------------------------------------------------------------------------

/** Baixa um blob de uma URL autenticada e dispara o download no navegador. */
async function downloadBlob(
  url: string,
  fallbackFilename: string,
  headers?: Record<string, string>,
): Promise<void> {
  const res = await fetch(url, { headers: headers ?? authHeaders() });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = extractDetail(body.detail, detail);
    } catch {
      // corpo nao-JSON
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const filename = match ? match[1] : fallbackFilename;
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
}

export async function exportDocuments(
  format: 'csv' | 'xlsx',
  params?: DocumentListParams,
): Promise<void> {
  const search = new URLSearchParams();
  search.set('format', format);
  if (params?.doc_type) search.set('doc_type', params.doc_type);
  if (params?.status) search.set('status', params.status);
  if (params?.import_id) search.set('import_id', params.import_id);
  if (params?.date_from) search.set('date_from', params.date_from);
  if (params?.date_to) search.set('date_to', params.date_to);
  if (params?.cliente_id) search.set('cliente_id', params.cliente_id);
  if (params?.ids && params.ids.length > 0) search.set('ids', params.ids.join(','));
  await downloadBlob(
    `/api/documents/export?${search.toString()}`,
    `documentos.${format}`,
  );
}

export async function downloadDocument(
  docId: string,
  format: 'json' | 'xml' | 'pdf' | 'fields',
  fallbackFilename: string,
): Promise<void> {
  await downloadBlob(
    `/api/documents/${docId}/download?format=${format}`,
    fallbackFilename,
  );
}

export async function updateDocumentFields(
  docId: string,
  fields: Record<string, unknown>,
): Promise<{ document_id: string; updated_fields: string[]; document: DocumentSummary }> {
  const res = await fetch(`/api/documents/${docId}/fields`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(fields),
  });
  return handle(res);
}

// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------

export interface AuditEntry {
  id: string;
  action: string;
  resource: string;
  details: Record<string, unknown>;
  user_id: string | null;
  organization_id: string | null;
  created_at: string | null;
}

export interface AuditListResponse {
  entries: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditListParams {
  action?: string;
  resource?: string;
  user_id?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export async function listAudit(params?: AuditListParams): Promise<AuditListResponse> {
  const search = new URLSearchParams();
  if (params?.action) search.set('action', params.action);
  if (params?.resource) search.set('resource', params.resource);
  if (params?.user_id) search.set('user_id', params.user_id);
  if (params?.date_from) search.set('date_from', params.date_from);
  if (params?.date_to) search.set('date_to', params.date_to);
  if (params?.limit) search.set('limit', String(params.limit));
  if (params?.offset) search.set('offset', String(params.offset));

  const qs = search.toString();
  const url = `/api/audit${qs ? `?${qs}` : ''}`;
  // Envia X-Organization-ID da org ativa quando disponivel (padrao
  // SefazSearch/certificados — fix auditoria S14): a role AUDIT_ROLES e
  // validada pelo backend NA org resolvida.
  const res = await fetch(url, { headers: orgBoundHeaders(getActiveOrgId()) });
  return handle<AuditListResponse>(res);
}

// Issue #550: export de auditoria via fetch autenticado + blob download
// (mesmo padrao de exportDocuments). A versao anterior montava uma URL com
// `?token=` para <a href> — mas o backend so le o header Authorization
// (HTTPBearer), entao o download sempre respondia 401.
export async function exportAudit(
  format: 'csv' | 'json',
  params?: AuditListParams,
): Promise<void> {
  const search = new URLSearchParams();
  search.set('format', format);
  if (params?.action) search.set('action', params.action);
  if (params?.date_from) search.set('date_from', params.date_from);
  if (params?.date_to) search.set('date_to', params.date_to);

  await downloadBlob(
    `/api/audit/export?${search.toString()}`,
    `auditoria_lgpd.${format}`,
    orgBoundHeaders(getActiveOrgId()),
  );
}

// ---------------------------------------------------------------------------
// Billing & Quota (Issue #245)
// ---------------------------------------------------------------------------

export interface SubscriptionInfo {
  plan: string;
  status: string;
  subscription_id?: string;
  current_period_start?: string;
  current_period_end?: string;
  suspended_at?: string | null;
  cancelled_at?: string | null;
  payment_method?: string | null;
  pending_invoices?: InvoiceInfo[];
}

export async function fetchSubscription(): Promise<SubscriptionInfo> {
  const res = await fetch('/api/billing/subscription', { headers: authHeaders() });
  return handle<SubscriptionInfo>(res);
}

// Issue #248: Gating de features por plano.
export interface PlanFeatures {
  upload: boolean;
  export_json: boolean;
  export_csv: boolean;
  multi_user: boolean;
  audit: boolean;
  api_access: boolean;
  priority_support: boolean;
  max_users: number;
  max_docs: number;
}

export interface FeaturesResponse {
  plan: string;
  features: PlanFeatures;
}

export async function fetchFeatures(): Promise<FeaturesResponse> {
  const res = await fetch('/api/billing/features', { headers: authHeaders() });
  return handle<FeaturesResponse>(res);
}

export interface QuotaInfo {
  plan: string;
  limit: number;
  used: number;
  remaining: number;
  reset_date: string;
}

export async function fetchQuota(): Promise<QuotaInfo> {
  const res = await fetch('/api/quota/usage', { headers: authHeaders() });
  return handle<QuotaInfo>(res);
}

// ---------------------------------------------------------------------------
// Billing — plans, subscribe, confirm payment, cancel (Sprint 7)
// ---------------------------------------------------------------------------

export interface PlanInfo {
  plan: string;
  name: string;
  price: number;
  currency: string;
  docs_per_month: number;
  // Issue #592/#593: plano contact-only (ex.: enterprise) nao tem checkout
  // self-service — o CTA e mailto vendas@, nunca /billing/subscribe.
  contact_only?: boolean;
  features: PlanFeatures;
}

// Issue #593: contact-only = flag `contact_only` da API (#592) OU a key
// 'enterprise' — defesa em profundidade para a janela em que o backend
// ainda nao envia a flag. Regra unica compartilhada por Pricing,
// Subscription e AdminPanel (cupons/checkout nunca incluem esses planos).
export function isContactOnlyPlan(p: Pick<PlanInfo, 'plan' | 'contact_only'>): boolean {
  return p.contact_only === true || p.plan === 'enterprise';
}

// Issue #593/F2: plano self-service = pago, nao-free e nao contact-only.
// Fonte unica das opcoes de checkout (Subscription) e de cupons elegiveis
// (AdminPanel) — funciona na janela legada ('pro') e no contrato #592
// ('single'/'team') sem hardcode de keys.
export function isSelfServicePlan(
  p: Pick<PlanInfo, 'plan' | 'price' | 'contact_only'>,
): boolean {
  return p.plan !== 'free' && p.price > 0 && !isContactOnlyPlan(p);
}

export interface PlansResponse {
  plans: PlanInfo[];
}

export async function fetchPlans(): Promise<PlansResponse> {
  const res = await fetch('/api/billing/plans');
  return handle<PlansResponse>(res);
}

export interface InvoiceInfo {
  invoice_id: string;
  subscription_id: string;
  period_start: string | null;
  period_end: string | null;
  amount: number;
  status: string;
  due_date: string | null;
  paid_at: string | null;
  // Issue #544: cupom aplicado na invoice (null/0 = sem desconto).
  coupon_id?: string | null;
  discount_amount?: number;
  created_at: string | null;
}

export interface SubscriptionDetail {
  subscription_id: string;
  plan: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  suspended_at: string | null;
  cancelled_at: string | null;
  payment_method: string | null;
}

export interface SubscribeResponse {
  subscription: SubscriptionDetail;
  invoice: InvoiceInfo;
  // Issue #544: null quando a invoice e cortesia (desconto de 100%).
  qr_code_base64: string | null;
  courtesy: boolean;
  next_invoice: InvoiceInfo | null;
}

export async function subscribe(
  plan: string,
  paymentMethod = 'pix',
  couponCode?: string | null,
): Promise<SubscribeResponse> {
  const res = await fetch('/api/billing/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      plan,
      payment_method: paymentMethod,
      coupon_code: couponCode || undefined,
    }),
  });
  return handle<SubscribeResponse>(res);
}

// ---------------------------------------------------------------------------
// Cupons de desconto (Issue #544, US-CPN-001)
// ---------------------------------------------------------------------------
//   GET    /api/coupons                    -> lista cupons (super_user)
//   POST   /api/coupons                    -> cria cupom (super_user)
//   PATCH  /api/coupons/{id}               -> atualiza cupom (super_user)
//   POST   /api/coupons/{id}/deactivate    -> desativa cupom (super_user)
//   POST   /api/coupons/validate           -> preview do desconto (autenticado)

export type CouponDiscountType = 'percent' | 'fixed';

export interface CouponInfo {
  coupon_id: string;
  code: string;
  discount_type: CouponDiscountType;
  value: number;
  expires_at: string | null;
  max_uses: number | null;
  max_uses_per_org: number;
  eligible_plans: string[] | null;
  is_active: boolean;
  uses_count: number;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CouponsResponse {
  coupons: CouponInfo[];
  count: number;
}

export interface CouponCreatePayload {
  code: string;
  discount_type: CouponDiscountType;
  value: number;
  expires_at?: string | null;
  max_uses?: number | null;
  max_uses_per_org?: number;
  eligible_plans?: string[] | null;
}

export interface CouponUpdatePayload {
  discount_type?: CouponDiscountType;
  value?: number;
  expires_at?: string | null;
  max_uses?: number | null;
  max_uses_per_org?: number;
  eligible_plans?: string[] | null;
  is_active?: boolean;
}

// Motivos de invalidade do /validate — mapeados para i18n no frontend.
// 'invalid' e o colapso anti-enumeracao de not_found/inactive
// (security review #544): a resposta nao revela se o codigo existe.
export type CouponInvalidReason =
  | 'invalid'
  | 'not_found'
  | 'inactive'
  | 'expired'
  | 'ineligible_plan'
  | 'exhausted'
  | 'org_exhausted'
  | 'invalid_plan';

export interface CouponValidation {
  valid: boolean;
  reason?: CouponInvalidReason;
  code?: string;
  discount_type?: CouponDiscountType;
  value?: number;
  plan?: string;
  original_price?: number;
  discounted_price?: number;
}

export async function listCoupons(): Promise<CouponsResponse> {
  const res = await fetch('/api/coupons', { headers: authHeaders() });
  return handle<CouponsResponse>(res);
}

export async function createCoupon(payload: CouponCreatePayload): Promise<CouponInfo> {
  const res = await fetch('/api/coupons', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload),
  });
  return handle<CouponInfo>(res);
}

export async function updateCoupon(id: string, payload: CouponUpdatePayload): Promise<CouponInfo> {
  const res = await fetch(`/api/coupons/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload),
  });
  return handle<CouponInfo>(res);
}

export async function deactivateCoupon(id: string): Promise<CouponInfo> {
  const res = await fetch(`/api/coupons/${id}/deactivate`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return handle<CouponInfo>(res);
}

export async function validateCoupon(code: string, plan: string): Promise<CouponValidation> {
  const res = await fetch('/api/coupons/validate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ code, plan }),
  });
  return handle<CouponValidation>(res);
}

export async function cancelSubscription(): Promise<{ message: string; active_until: string | null }> {
  const res = await fetch('/api/billing/cancel', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  return handle<{ message: string; active_until: string | null }>(res);
}

export async function confirmPayment(invoiceId: string): Promise<{ invoice: InvoiceInfo; subscription: SubscriptionDetail; next_invoice: InvoiceInfo }> {
  const res = await fetch('/api/billing/confirm-payment', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ invoice_id: invoiceId }),
  });
  return handle<{ invoice: InvoiceInfo; subscription: SubscriptionDetail; next_invoice: InvoiceInfo }>(res);
}

// ---------------------------------------------------------------------------
// Stripe Checkout recorrente (Issue #428)
// ---------------------------------------------------------------------------

export interface StripeSubscriptionStatus {
  subscription_id?: string | null;
  plan: string;
  status: string;
  current_period_start?: string | null;
  current_period_end?: string | null;
  suspended_at?: string | null;
  cancelled_at?: string | null;
  payment_method?: string | null;
}

export interface CreateCheckoutResponse {
  checkout_url: string;
  session_id: string;
  subscription: StripeSubscriptionStatus;
}

export async function createStripeCheckout(plan: string): Promise<CreateCheckoutResponse> {
  const res = await fetch('/api/subscriptions/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ plan }),
  });
  return handle<CreateCheckoutResponse>(res);
}

export async function fetchStripeSubscriptionStatus(): Promise<StripeSubscriptionStatus> {
  const res = await fetch('/api/subscriptions/status', { headers: authHeaders() });
  return handle<StripeSubscriptionStatus>(res);
}

export async function cancelStripeSubscription(): Promise<{ message: string; active_until: string | null; subscription: StripeSubscriptionStatus }> {
  const res = await fetch('/api/subscriptions/cancel', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  return handle<{ message: string; active_until: string | null; subscription: StripeSubscriptionStatus }>(res);
}

export async function createStripePortalSession(): Promise<{ portal_url: string }> {
  const res = await fetch('/api/subscriptions/portal', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  return handle<{ portal_url: string }>(res);
}

// ---------------------------------------------------------------------------
// Auth — signup self-service (S7-001)
// ---------------------------------------------------------------------------

export interface SignupResponse {
  message: string;
}

export async function signup(
  name: string,
  email: string,
  password: string,
  companyName: string,
  consentAccepted: boolean,
): Promise<SignupResponse> {
  const res = await fetch('/api/auth/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      email,
      password,
      company_name: companyName,
      consent_accepted: consentAccepted,
    }),
  });
  return handle<SignupResponse>(res);
}

// ---------------------------------------------------------------------------
// Auth — accept team invite (Issue #411)
// ---------------------------------------------------------------------------

export interface AcceptInviteResponse {
  message: string;
  status: string;
  user_id?: string;
}

// Aceita um convite de equipe: valida o token, cria/atualiza o usuario e o
// adiciona a organizacao convidada. Diferente de handle(), anexa o status
// HTTP ao erro para que a UI possa distinguir 404 (invalido) de 410 (expirado).
export async function acceptInvite(
  token: string,
  name: string | undefined,
  password: string,
  consentAccepted: boolean,
): Promise<AcceptInviteResponse> {
  const res = await fetch('/api/auth/accept-invite', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      token,
      name: name || null,
      password,
      consent_accepted: consentAccepted,
    }),
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = extractDetail(body.detail, detail);
    } catch {
      // corpo nao-JSON; mantem o status
    }
    throw Object.assign(new Error(detail), { status: res.status });
  }
  return (await res.json()) as AcceptInviteResponse;
}

// ---------------------------------------------------------------------------
// Notifications (Issue #281)
// ---------------------------------------------------------------------------

export interface NotificationItem {
  id: string;
  title: string;
  body: string | null;
  type: string;
  is_read: boolean;
  created_at: string | null;
  read_at: string | null;
  metadata: Record<string, unknown> | null;
}

export interface NotificationsResponse {
  notifications: NotificationItem[];
  unread_count: number;
}

export async function getNotifications(): Promise<NotificationsResponse> {
  const res = await fetch('/api/notifications', { headers: authHeaders() });
  return handle<NotificationsResponse>(res);
}

export async function markNotificationRead(notifId: string): Promise<void> {
  const res = await fetch(`/api/notifications/${notifId}/read`, {
    method: 'PUT',
    headers: authHeaders(),
  });
  await handle<{ ok: boolean }>(res);
}

export async function markAllNotificationsRead(): Promise<void> {
  const res = await fetch('/api/notifications/read-all', {
    method: 'PUT',
    headers: authHeaders(),
  });
  await handle<{ ok: boolean }>(res);
}

// ---------------------------------------------------------------------------
// Team / Members (Issue #328)
// ---------------------------------------------------------------------------
// Endpoints esperados do backend (RBAC: admin/super_user):
//   GET    /api/team/members          -> lista membros da org do usuario
//   POST   /api/team/invite           -> convida membro (email + role)
//   PATCH  /api/team/members/{id}     -> altera role de um membro
//   DELETE /api/team/members/{id}     -> remove um membro

// S10-T02: perfis operacionais multi-org (revisor/financeiro/operador/
// auditor) somados aos legados admin/member/contador. "parceiro" so pode
// ser atribuido por super_user e "super_user" nunca via API (issue #347).
export type MemberRole =
  | 'admin'
  | 'member'
  | 'contador'
  | 'revisor'
  | 'financeiro'
  | 'operador'
  | 'auditor';

export interface TeamMember {
  id: string;
  email: string;
  name: string | null;
  role: string;
  status: string;
  // S10-T01: status real do usuario (desativacao admin).
  is_active?: boolean;
  created_at: string | null;
}

export interface TeamMembersResponse {
  members: TeamMember[];
}

export interface InviteResponse {
  message: string;
}

export async function listTeamMembers(): Promise<TeamMembersResponse> {
  const res = await fetch('/api/team/members', { headers: authHeaders() });
  return handle<TeamMembersResponse>(res);
}

export async function inviteMember(email: string, role: MemberRole): Promise<InviteResponse> {
  const res = await fetch('/api/team/invite', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ email, role }),
  });
  return handle<InviteResponse>(res);
}

export async function updateMemberRole(memberId: string, role: MemberRole): Promise<{ message: string }> {
  const res = await fetch(`/api/team/members/${memberId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ role }),
  });
  return handle<{ message: string }>(res);
}

export async function removeMember(memberId: string): Promise<{ message: string }> {
  const res = await fetch(`/api/team/members/${memberId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return handle<{ message: string }>(res);
}

// ---------------------------------------------------------------------------
// Team / Invites (Issue #413) — pending invitations
// ---------------------------------------------------------------------------
//   GET    /api/team/invites              -> lista convites pendentes
//   DELETE /api/team/invites/{id}         -> cancela convite
//   POST   /api/team/invites/{id}/resend  -> reenvia email do convite

export interface PendingInvite {
  id: string;
  email: string;
  role: string;
  created_at: string | null;
  expires_at: string | null;
}

export interface PendingInvitesResponse {
  invites: PendingInvite[];
}

export async function listPendingInvites(): Promise<PendingInvitesResponse> {
  const res = await fetch('/api/team/invites', { headers: authHeaders() });
  return handle<PendingInvitesResponse>(res);
}

// ---------------------------------------------------------------------------
// Team — acoes administrativas de membros (S10-T01)
// ---------------------------------------------------------------------------
//   POST /api/team/members/{id}/deactivate      -> desativa usuario
//   POST /api/team/members/{id}/activate        -> reativa usuario
//   POST /api/team/members/{id}/reset-password  -> senha temporaria (1x)

export interface MemberStatusResponse {
  message: string;
  is_active: boolean;
}

export interface ResetPasswordResponse {
  message: string;
  temporary_password: string;
}

export async function deactivateMember(memberId: string): Promise<MemberStatusResponse> {
  const res = await fetch(`/api/team/members/${memberId}/deactivate`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return handle<MemberStatusResponse>(res);
}

export async function activateMember(memberId: string): Promise<MemberStatusResponse> {
  const res = await fetch(`/api/team/members/${memberId}/activate`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return handle<MemberStatusResponse>(res);
}

export async function resetMemberPassword(memberId: string): Promise<ResetPasswordResponse> {
  const res = await fetch(`/api/team/members/${memberId}/reset-password`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return handle<ResetPasswordResponse>(res);
}

export async function cancelInvite(inviteId: string): Promise<{ message: string }> {
  const res = await fetch(`/api/team/invites/${inviteId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return handle<{ message: string }>(res);
}

export async function resendInvite(inviteId: string): Promise<{ message: string }> {
  const res = await fetch(`/api/team/invites/${inviteId}/resend`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return handle<{ message: string }>(res);
}

// ---------------------------------------------------------------------------
// Admin Panel (Issue #332) — super_user only
// ---------------------------------------------------------------------------
//   GET /api/admin/orgs     -> todas as orgs com metricas
//   GET /api/admin/users    -> todos os usuarios com org e role
//   GET /api/admin/metrics  -> metricas globais

export interface AdminOrg {
  id: string;
  name: string;
  slug: string;
  docs_count: number;
  users_count: number;
  status: string;
  is_test?: boolean;
  created_at: string | null;
}

export interface AdminOrgsResponse {
  orgs: AdminOrg[];
}

export interface AdminUser {
  id: string;
  email: string;
  role: string;
  org_name: string | null;
  is_active: boolean;
  created_at: string | null;
}

export interface AdminUsersResponse {
  users: AdminUser[];
}

export interface AdminMetrics {
  total_docs: number;
  total_errors: number;
  error_rate: number;
  active_orgs: number;
  total_orgs: number;
  total_users: number;
}

export async function listAdminOrgs(includeTest = false): Promise<AdminOrgsResponse> {
  const qs = includeTest ? '?include_test=true' : '';
  const res = await fetch(`/api/admin/orgs${qs}`, { headers: authHeaders() });
  return handle<AdminOrgsResponse>(res);
}

export async function listAdminUsers(includeTest = false): Promise<AdminUsersResponse> {
  const qs = includeTest ? '?include_test=true' : '';
  const res = await fetch(`/api/admin/users${qs}`, { headers: authHeaders() });
  return handle<AdminUsersResponse>(res);
}

export async function fetchAdminMetrics(includeTest = false): Promise<AdminMetrics> {
  const qs = includeTest ? '?include_test=true' : '';
  const res = await fetch(`/api/admin/metrics${qs}`, { headers: authHeaders() });
  return handle<AdminMetrics>(res);
}

// ---------------------------------------------------------------------------
// Clientes do contador (Issue #329) — separacao de telas por cliente
// ---------------------------------------------------------------------------
//   GET    /api/clientes           -> lista clientes da org ativa
//   POST   /api/clientes           -> cria cliente
//   PATCH  /api/clientes/{id}      -> atualiza cliente
//   DELETE /api/clientes/{id}      -> soft-delete (is_active=false)

export interface Cliente {
  id: string;
  organization_id: string;
  nome: string;
  cnpj: string | null;
  email: string | null;
  telefone: string | null;
  is_active: boolean;
  docs_count: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface ClientesResponse {
  clientes: Cliente[];
}

export interface ClienteCreatePayload {
  nome: string;
  cnpj?: string | null;
  email?: string | null;
  telefone?: string | null;
}

export interface ClienteUpdatePayload {
  nome?: string;
  cnpj?: string | null;
  email?: string | null;
  telefone?: string | null;
  is_active?: boolean;
}

export async function listClientes(includeInactive = false): Promise<ClientesResponse> {
  const url = `/api/clientes${includeInactive ? '?include_inactive=true' : ''}`;
  const res = await fetch(url, { headers: authHeaders() });
  return handle<ClientesResponse>(res);
}

export async function createCliente(payload: ClienteCreatePayload): Promise<Cliente> {
  const res = await fetch('/api/clientes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload),
  });
  return handle<Cliente>(res);
}

export async function updateCliente(id: string, payload: ClienteUpdatePayload): Promise<Cliente> {
  const res = await fetch(`/api/clientes/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload),
  });
  return handle<Cliente>(res);
}

export async function deactivateCliente(id: string): Promise<{ message: string; id: string }> {
  const res = await fetch(`/api/clientes/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return handle<{ message: string; id: string }>(res);
}

// ---------------------------------------------------------------------------
// Impersonation (Issue #330) — super_user "acessar como" tenant
// ---------------------------------------------------------------------------
//   POST /api/auth/impersonate/{org_id}  -> inicia impersonation
//   POST /api/auth/impersonate/exit     -> encerra impersonation

const ORIGINAL_TOKEN_KEY = 'auranota_token_original';

export function saveOriginalToken(): void {
  const current = getToken();
  if (current) {
    localStorage.setItem(ORIGINAL_TOKEN_KEY, current);
  }
}

export function getOriginalToken(): string | null {
  return localStorage.getItem(ORIGINAL_TOKEN_KEY);
}

export function clearOriginalToken(): void {
  localStorage.removeItem(ORIGINAL_TOKEN_KEY);
}

export function isImpersonating(): boolean {
  return getOriginalToken() !== null;
}

export async function impersonateOrg(orgId: string): Promise<{ access_token: string; token_type: string }> {
  const res = await fetch(`/api/auth/impersonate/${orgId}`, {
    method: 'POST',
    headers: authHeaders(),
  });
  const data = await handle<{ access_token: string; token_type: string }>(res);
  // Salva token original antes de substituir pelo de impersonation.
  saveOriginalToken();
  setToken(data.access_token);
  return data;
}

export async function exitImpersonation(): Promise<{ access_token: string; token_type: string }> {
  const res = await fetch('/api/auth/impersonate/exit', {
    method: 'POST',
    headers: authHeaders(),
  });
  const data = await handle<{ access_token: string; token_type: string }>(res);
  // Restaura token normal e limpa o original.
  setToken(data.access_token);
  clearOriginalToken();
  return data;
}

// ---------------------------------------------------------------------------
// PayPal Checkout recorrente (Issue #429, S9-T17)
// ---------------------------------------------------------------------------

export interface PaypalCheckoutResponse {
  approval_url: string;
  paypal_subscription_id: string;
  subscription: StripeSubscriptionStatus;
}

export async function createPaypalCheckout(plan: string): Promise<PaypalCheckoutResponse> {
  const res = await fetch('/api/subscriptions/paypal/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ plan }),
  });
  return handle<PaypalCheckoutResponse>(res);
}

export async function fetchPaypalSubscriptionStatus(): Promise<StripeSubscriptionStatus> {
  const res = await fetch('/api/subscriptions/paypal/status', { headers: authHeaders() });
  return handle<StripeSubscriptionStatus>(res);
}

export async function cancelPaypalSubscription(): Promise<{ message: string; active_until: string | null; subscription: StripeSubscriptionStatus }> {
  const res = await fetch('/api/subscriptions/paypal/cancel', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
  });
  return handle<{ message: string; active_until: string | null; subscription: StripeSubscriptionStatus }>(res);
}

// ---------------------------------------------------------------------------
// PIX automatico com BR Code real (Issue #430, S9-T16)
// ---------------------------------------------------------------------------

export interface PixCheckoutResponse {
  subscription: StripeSubscriptionStatus;
  invoice: InvoiceInfo;
  txid: string;
  br_code: string;
  qr_code_base64: string;
  expires_at: string;
}

export interface PixPaymentStatus {
  txid: string;
  paid: boolean;
  subscription: StripeSubscriptionStatus | null;
}

export async function createPixCheckout(plan: string): Promise<PixCheckoutResponse> {
  const res = await fetch('/api/subscriptions/pix/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ plan }),
  });
  return handle<PixCheckoutResponse>(res);
}

export async function fetchPixPaymentStatus(txid: string): Promise<PixPaymentStatus> {
  const res = await fetch(`/api/subscriptions/pix/status/${txid}`, { headers: authHeaders() });
  return handle<PixPaymentStatus>(res);
}

// ---------------------------------------------------------------------------
// Certificates — custodia de certificado digital A1 (Sprint 14, S14-T07)
// ---------------------------------------------------------------------------
// Contrato REST implementado pelo backend (app/routers/certificates.py):
//   POST   /api/certificates          upload multipart (file + senha + consent_accepted)
//   GET    /api/certificates          {certificate: {...}} (404 = NO_CERTIFICATE)
//   GET    /api/certificates/status   {has_certificate, status, valid_to, dias_para_vencer}
//   POST   /api/certificates/rotate   rotacao (multipart + confirm_cnpj_mismatch)
//   DELETE /api/certificates          remocao (body {confirm:true})
//   GET    /api/certificates/history  trilha paginada por limit/offset (AUDIT_ROLES)
// Erros seguem {detail:{error, code, retryAfterSec?}} — codes reais do
// backend: INVALID_PFX, INVALID_CERT_CHAIN, CERT_EXPIRED, PFX_TOO_LARGE,
// VALIDATION_ERROR, CERT_INVALID, CERT_UPLOAD_DISABLED, CONSENT_REQUIRED,
// NO_ORG_CNPJ, CERT_ALREADY_EXISTS, CNPJ_MISMATCH, NO_CERTIFICATE,
// CONFIRM_REQUIRED, CERT_RATE_LIMITED, SESSION_RATE_LIMITED,
// MCP_UNAVAILABLE/TRANSPORT/MCP_ERROR/SESSION_ERROR.
// Invariante: nenhuma resposta carrega PFX, senha, session_token, PEM ou
// chave privada — apenas metadados.

export interface CertificateMetadata {
  id: string | null;
  cnpj: string | null;
  cn: string | null;
  issuer: string | null;
  serial: string | null;
  valid_from: string | null;
  valid_to: string | null;
  fingerprint_sha256: string | null;
  status: string;
  // uploaded_by e o UUID do usuario (o backend nao devolve e-mail — pendencia
  // registrada); uploaded_by_email fica null ate o backend expor o campo.
  uploaded_by: string | null;
  uploaded_by_email: string | null;
  created_at: string | null;
  last_session_at: string | null;
  last_verified_at: string | null;
  removed_at: string | null;
  days_until_expiry: number | null;
}

export interface CertificateStatusResponse {
  has_certificate: boolean;
  status: 'ativo' | 'vencendo' | 'expirado' | 'pendente_consentimento' | 'ausente' | string;
  valid_to?: string | null;
  days_until_expiry?: number | null;
}

export interface CertificateHistoryItem {
  action: string;
  user_id: string | null;
  user_email: string | null;
  timestamp: string | null;
  details: Record<string, unknown>;
}

export interface CertificateHistoryResponse {
  items: CertificateHistoryItem[];
  total: number;
}

// Erro tipado dos endpoints de certificado. `code` vem de
// {detail:{error, code}} (ex.: INVALID_PFX, CNPJ_MISMATCH, CERT_RATE_LIMITED);
// `retryAfterSec` acompanha 429 para exibir o cooldown.
export class CertificateApiError extends Error {
  status: number;
  code: string | null;
  retryAfterSec?: number;
  constructor(status: number, code: string | null, message: string, retryAfterSec?: number) {
    super(message);
    this.name = 'CertificateApiError';
    this.status = status;
    this.code = code;
    this.retryAfterSec = retryAfterSec;
  }
}

// Os helpers de org ativa (getActiveOrgId / orgBoundHeaders) ficam no topo
// do arquivo, junto de authHeaders — sao compartilhados por todas as
// secoes org-scoped (certificados, auditoria).

// handle() especifico: preserva status HTTP, code e retryAfterSec para que a
// UI possa mapear erros (mapa §8.5) e tratar 404/409/429 sem parsing ad-hoc.
async function handleCertificate<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let code: string | null = null;
    let message = `${res.status} ${res.statusText}`;
    let retryAfterSec: number | undefined;
    try {
      const body = await res.json();
      const detail = body?.detail;
      if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        const d = detail as Record<string, unknown>;
        if (typeof d.code === 'string') code = d.code;
        if (typeof d.retryAfterSec === 'number') retryAfterSec = d.retryAfterSec;
        // O router de certificados usa `error` (nao `message`) para o texto.
        message = typeof d.error === 'string' ? d.error : extractDetail(detail, message);
      } else {
        message = extractDetail(detail, message);
      }
    } catch {
      // corpo nao-JSON; mantem o status
    }
    // Retry-After header como fallback do retryAfterSec do body (429).
    if (res.status === 429 && retryAfterSec === undefined) {
      const header = res.headers.get('Retry-After');
      const parsed = header ? Number(header) : NaN;
      if (!Number.isNaN(parsed)) retryAfterSec = parsed;
    }
    interceptGlobalErrors(res, message);
    throw new CertificateApiError(res.status, code, message, retryAfterSec);
  }
  // DELETE pode retornar 204 sem corpo.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// GET/POST/rotate respondem {"certificate": {...}} — desembrulha e
// normaliza os campos divergentes do backend (dias_para_vencer ->
// days_until_expiry; uploaded_by -> UUID, sem e-mail).
function normalizeCertificate(body: unknown): CertificateMetadata {
  const envelope = (body ?? {}) as Record<string, unknown>;
  const raw = (
    envelope.certificate && typeof envelope.certificate === 'object'
      ? envelope.certificate
      : envelope
  ) as Record<string, unknown>;
  const cert = raw as unknown as CertificateMetadata;
  const dias = raw.dias_para_vencer ?? raw.days_until_expiry;
  cert.days_until_expiry = typeof dias === 'number' ? dias : null;
  cert.uploaded_by = (raw.uploaded_by as string | null) ?? null;
  cert.uploaded_by_email = (raw.uploaded_by_email as string | null) ?? null;
  return cert;
}

// GET /status: {has_certificate, status, valid_to, dias_para_vencer} —
// deriva 'ausente' (sem certificado) e 'vencendo' (ativo a <=30 dias) para a
// badge funcional member-safe.
function normalizeCertificateStatus(body: unknown): CertificateStatusResponse {
  const raw = (body ?? {}) as Record<string, unknown>;
  const has = raw.has_certificate !== false;
  const dias = raw.dias_para_vencer ?? raw.days_until_expiry;
  let status = typeof raw.status === 'string' ? raw.status : 'ausente';
  if (!has || status === 'sem_certificado') {
    status = 'ausente';
  } else if (status === 'ativo' && typeof dias === 'number' && dias >= 0 && dias <= 30) {
    status = 'vencendo';
  }
  return {
    has_certificate: has,
    status,
    valid_to: (raw.valid_to as string | null) ?? null,
    days_until_expiry: typeof dias === 'number' ? dias : null,
  };
}

export async function uploadCertificate(
  file: File,
  senha: string,
  consentAccepted: boolean,
  orgId?: string | null,
): Promise<CertificateMetadata> {
  const form = new FormData();
  form.append('file', file);
  form.append('senha', senha);
  form.append('consent_accepted', String(consentAccepted));
  const res = await fetch('/api/certificates', {
    method: 'POST',
    // Sem Content-Type manual: o browser define multipart/form-data + boundary.
    headers: orgBoundHeaders(orgId),
    body: form,
  });
  return normalizeCertificate(await handleCertificate<unknown>(res));
}

export async function getCertificate(orgId?: string | null): Promise<CertificateMetadata> {
  const res = await fetch('/api/certificates', { headers: orgBoundHeaders(orgId) });
  return normalizeCertificate(await handleCertificate<unknown>(res));
}

export async function getCertificateStatus(orgId?: string | null): Promise<CertificateStatusResponse> {
  const res = await fetch('/api/certificates/status', { headers: orgBoundHeaders(orgId) });
  return normalizeCertificateStatus(await handleCertificate<unknown>(res));
}

export async function rotateCertificate(
  file: File,
  senha: string,
  consentAccepted: boolean,
  confirmCnpjMismatch = false,
  orgId?: string | null,
): Promise<CertificateMetadata> {
  const form = new FormData();
  form.append('file', file);
  form.append('senha', senha);
  form.append('consent_accepted', String(consentAccepted));
  form.append('confirm_cnpj_mismatch', String(confirmCnpjMismatch));
  const res = await fetch('/api/certificates/rotate', {
    method: 'POST',
    headers: orgBoundHeaders(orgId),
    body: form,
  });
  return normalizeCertificate(await handleCertificate<unknown>(res));
}

export async function deleteCertificate(orgId?: string | null): Promise<void> {
  const res = await fetch('/api/certificates', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...orgBoundHeaders(orgId) },
    body: JSON.stringify({ confirm: true }),
  });
  await handleCertificate<{ status?: string }>(res);
}

// O backend pagina por limit/offset (default 50, max 200) e devolve itens
// {id, action, resource, resource_id, details, user_id, created_at} —
// converte page/size e mapeia created_at -> timestamp. user_email fica
// null (o backend devolve so user_id — pendencia registrada).
export async function getCertificateHistory(
  page = 1,
  size = 20,
  orgId?: string | null,
): Promise<CertificateHistoryResponse> {
  const limit = Math.min(Math.max(1, size), 200);
  const offset = Math.max(0, page - 1) * limit;
  const search = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const res = await fetch(`/api/certificates/history?${search.toString()}`, {
    headers: orgBoundHeaders(orgId),
  });
  const body = await handleCertificate<{ items?: unknown[]; total?: number }>(res);
  const items = (body.items ?? []).map((item): CertificateHistoryItem => {
    const it = (item ?? {}) as Record<string, unknown>;
    return {
      action: typeof it.action === 'string' ? it.action : '',
      user_id: (it.user_id as string | null) ?? null,
      user_email: (it.user_email as string | null) ?? null,
      timestamp: (it.created_at as string | null) ?? (it.timestamp as string | null) ?? null,
      details: (it.details as Record<string, unknown>) ?? {},
    };
  });
  return { items, total: typeof body.total === 'number' ? body.total : items.length };
}

// ---------------------------------------------------------------------------
// Org Settings — painel administrativo do tenant (S10-T01)
// ---------------------------------------------------------------------------
//   GET /api/org-settings -> {org_id, smtp, retention, thresholds}
//   PUT /api/org-settings -> update parcial por secao (smtp/retention/thresholds)

export interface OrgSmtpSettings {
  host: string | null;
  port: number;
  username: string | null;
  from_address: string | null;
  use_tls: boolean;
  enabled: boolean;
  password_set: boolean;
}

export interface OrgRetentionSettings {
  retention_days: number;
}

export interface OrgThresholds {
  vlm_confidence: number;
  auto_approve: number;
  needs_review: number;
}

export interface OrgSettings {
  org_id: string;
  smtp: OrgSmtpSettings;
  retention: OrgRetentionSettings;
  thresholds: OrgThresholds;
}

export interface OrgSettingsUpdatePayload {
  smtp?: {
    host?: string | null;
    port?: number;
    username?: string | null;
    password?: string;
    from_address?: string | null;
    use_tls?: boolean;
    enabled?: boolean;
  };
  retention_days?: number;
  thresholds?: Partial<OrgThresholds>;
}

export interface OrgSettingsUpdateResponse extends OrgSettings {
  updated: string[];
}

export async function getOrgSettings(): Promise<OrgSettings> {
  const res = await fetch('/api/org-settings', { headers: authHeaders() });
  return handle<OrgSettings>(res);
}

export async function updateOrgSettings(
  payload: OrgSettingsUpdatePayload,
): Promise<OrgSettingsUpdateResponse> {
  const res = await fetch('/api/org-settings', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload),
  });
  return handle<OrgSettingsUpdateResponse>(res);
}

// ---------------------------------------------------------------------------
// Email Config — monitoramento IMAP por organizacao (Issue #401)
// ---------------------------------------------------------------------------
//   GET    /api/email-config           -> lista configs IMAP da org ativa
//   POST   /api/email-config           -> cria config
//   PUT    /api/email-config/{id}      -> atualiza config
//   DELETE /api/email-config/{id}      -> remove config
//   POST   /api/email-config/{id}/test -> testa conexao IMAP

export interface EmailConfigItem {
  id: string;
  org_id: string;
  host: string;
  port: number;
  username: string;
  folder: string;
  filters: Record<string, unknown> | null;
  allowed_senders: string[] | null;
  accept_any_sender: boolean;
  max_email_size_mb: number;
  max_emails_per_poll: number;
  enabled: boolean;
  last_poll_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface EmailConfigCreatePayload {
  host: string;
  port?: number;
  username: string;
  password: string;
  folder?: string;
  filters?: Record<string, unknown> | null;
  allowed_senders?: string[] | null;
  accept_any_sender?: boolean;
  max_email_size_mb?: number;
  max_emails_per_poll?: number;
  enabled?: boolean;
}

export type EmailConfigUpdatePayload = Partial<EmailConfigCreatePayload>;

export async function listEmailConfigs(): Promise<{ configs: EmailConfigItem[] }> {
  const res = await fetch('/api/email-config', { headers: authHeaders() });
  return handle<{ configs: EmailConfigItem[] }>(res);
}

export async function createEmailConfig(payload: EmailConfigCreatePayload): Promise<EmailConfigItem> {
  const res = await fetch('/api/email-config', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload),
  });
  return handle<EmailConfigItem>(res);
}

export async function updateEmailConfig(
  configId: string,
  payload: EmailConfigUpdatePayload,
): Promise<EmailConfigItem> {
  const res = await fetch(`/api/email-config/${configId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload),
  });
  return handle<EmailConfigItem>(res);
}

export async function deleteEmailConfig(configId: string): Promise<{ message: string; id: string }> {
  const res = await fetch(`/api/email-config/${configId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return handle<{ message: string; id: string }>(res);
}

export async function testEmailConfig(
  configId: string,
): Promise<{ success: boolean; message?: string; error?: string }> {
  const res = await fetch(`/api/email-config/${configId}/test`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return handle<{ success: boolean; message?: string; error?: string }>(res);
}

// Captura dedicada por tenant — Issue #555:
//   GET  /api/email-config/capture            -> endereco dedicado da org
//                                                (lazy-create no 1o acesso)
//   POST /api/email-config/capture/regenerate -> rotaciona o token (invalida
//                                                o endereco anterior)
// Ambos MANAGE_ROLES no backend. A row mode='capture' nunca aparece no
// CRUD IMAP acima — endereco so existe/muda por estes endpoints.

export interface CaptureAddressItem {
  id: string;
  org_id: string;
  mode: string;
  capture_address: string | null;
  // Issue #570 — allowlist da captura dedicada: extras cadastrados pelo
  // admin. Membros ativos da org sao sempre permitidos (members_auto_allowed)
  // — a lista de e-mails de membros NAO e exposta pela API (privacidade).
  allowed_senders_extra: string[];
  members_auto_allowed: boolean;
  enabled: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export async function getCaptureAddress(): Promise<CaptureAddressItem> {
  const res = await fetch('/api/email-config/capture', { headers: authHeaders() });
  return handle<CaptureAddressItem>(res);
}

export async function regenerateCaptureAddress(): Promise<CaptureAddressItem> {
  const res = await fetch('/api/email-config/capture/regenerate', {
    method: 'POST',
    headers: authHeaders(),
  });
  return handle<CaptureAddressItem>(res);
}

// Issue #570 — gestao da allowlist da captura dedicada (MANAGE_ROLES):
//   POST   /api/email-config/capture/senders           -> adiciona extra
//   DELETE /api/email-config/capture/senders/{email}   -> remove extra
// Somente e-mails individuais — dominios/wildcards sao rejeitados (422)
// pelo backend; duplicado -> 409. Ambas retornam o CaptureAddressItem
// atualizado.

export async function addCaptureSender(email: string): Promise<CaptureAddressItem> {
  const res = await fetch('/api/email-config/capture/senders', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ email }),
  });
  return handle<CaptureAddressItem>(res);
}

export async function removeCaptureSender(email: string): Promise<CaptureAddressItem> {
  const res = await fetch(
    `/api/email-config/capture/senders/${encodeURIComponent(email)}`,
    {
      method: 'DELETE',
      headers: authHeaders(),
    },
  );
  return handle<CaptureAddressItem>(res);
}

// ---------------------------------------------------------------------------
// Municipios — catalogo municipio -> provedor NFS-e (Issue #420)
// ---------------------------------------------------------------------------
//   GET    /api/municipios                    -> lista (filtros uf/provedor/busca)
//   POST   /api/municipios                    -> cria (MANAGE_ROLES)
//   PUT    /api/municipios/{codigo_ibge}      -> atualiza (MANAGE_ROLES)
//   DELETE /api/municipios/{codigo_ibge}      -> remove (MANAGE_ROLES)

export interface MunicipioItem {
  id: number;
  codigo_ibge: string;
  nome: string;
  uf: string;
  provedor: string;
  agregador: string | null;
}

export interface MunicipioCreatePayload {
  codigo_ibge: string;
  nome: string;
  uf: string;
  provedor: string;
  agregador?: string | null;
}

export type MunicipioPayload = Partial<MunicipioCreatePayload>;

export async function listMunicipios(params?: {
  uf?: string;
  provedor?: string;
  busca?: string;
}): Promise<MunicipioItem[]> {
  const search = new URLSearchParams();
  if (params?.uf) search.set('uf', params.uf);
  if (params?.provedor) search.set('provedor', params.provedor);
  if (params?.busca) search.set('busca', params.busca);
  const qs = search.toString();
  const res = await fetch(`/api/municipios${qs ? `?${qs}` : ''}`, { headers: authHeaders() });
  return handle<MunicipioItem[]>(res);
}

export async function createMunicipio(payload: MunicipioCreatePayload): Promise<MunicipioItem> {
  const res = await fetch('/api/municipios', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload),
  });
  return handle<MunicipioItem>(res);
}

export async function updateMunicipio(
  codigoIbge: string,
  payload: MunicipioPayload,
): Promise<MunicipioItem> {
  const res = await fetch(`/api/municipios/${codigoIbge}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(payload),
  });
  return handle<MunicipioItem>(res);
}

export async function deleteMunicipio(codigoIbge: string): Promise<void> {
  const res = await fetch(`/api/municipios/${codigoIbge}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.detail) detail = extractDetail(body.detail, detail);
    } catch {
      // corpo nao-JSON; mantem o status
    }
    throw new Error(detail);
  }
}

// ---------------------------------------------------------------------------
// Catálogo colaborativo de municípios/provedores (S10-T06, US-S10-006)
// ---------------------------------------------------------------------------

export interface Municipio {
  id: number;
  codigo_ibge: string;
  nome: string;
  uf: string;
  provedor: string;
  agregador: string | null;
}

export interface CatalogoSugestao {
  id: string;
  organization_id: string;
  user_id: string;
  codigo_ibge: string;
  nome: string;
  uf: string;
  provedor: string;
  agregador: string | null;
  justificativa: string | null;
  status: 'pendente' | 'aprovada' | 'rejeitada';
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CatalogoStats {
  municipios_cobertos: number;
  provedores_suportados: number;
  agregadores: number;
  cobertura_por_uf: { uf: string; municipios: number }[];
  sugestoes_pendentes: number;
}

export interface CatalogoHistoryItem {
  id: string;
  codigo_ibge: string;
  action: 'create' | 'update' | 'delete' | string;
  old_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  origem: 'admin' | 'sugestao' | 'import' | string;
  sugestao_id: string | null;
  changed_by: string | null;
  organization_id: string | null;
  created_at: string | null;
}

export interface CatalogoExport {
  version: number;
  source: string;
  count: number;
  municipios: Omit<Municipio, 'id'>[];
}

export interface CatalogoImportResult {
  created: number;
  updated: number;
  total: number;
}

export async function fetchMunicipios(params?: {
  uf?: string;
  provedor?: string;
  busca?: string;
}): Promise<Municipio[]> {
  const q = new URLSearchParams();
  if (params?.uf) q.set('uf', params.uf);
  if (params?.provedor) q.set('provedor', params.provedor);
  if (params?.busca) q.set('busca', params.busca);
  const qs = q.toString();
  const res = await fetch(`/api/municipios${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(),
  });
  return handle<Municipio[]>(res);
}

export async function fetchCatalogoStats(): Promise<CatalogoStats> {
  const res = await fetch('/api/catalogo/stats', { headers: authHeaders() });
  return handle<CatalogoStats>(res);
}

export async function fetchCatalogoSugestoes(status?: string): Promise<CatalogoSugestao[]> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  const res = await fetch(`/api/catalogo/sugestoes${qs}`, { headers: authHeaders() });
  return handle<CatalogoSugestao[]>(res);
}

export async function createCatalogoSugestao(body: {
  codigo_ibge: string;
  nome: string;
  uf: string;
  provedor: string;
  agregador?: string | null;
  justificativa?: string | null;
}): Promise<CatalogoSugestao> {
  const res = await fetch('/api/catalogo/sugestoes', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  return handle<CatalogoSugestao>(res);
}

export async function approveCatalogoSugestao(
  id: string,
): Promise<{ sugestao: CatalogoSugestao; municipio: Municipio }> {
  const res = await fetch(`/api/catalogo/sugestoes/${id}/aprovar`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return handle<{ sugestao: CatalogoSugestao; municipio: Municipio }>(res);
}

export async function rejectCatalogoSugestao(
  id: string,
  note?: string,
): Promise<CatalogoSugestao> {
  const res = await fetch(`/api/catalogo/sugestoes/${id}/rejeitar`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ note: note ?? null }),
  });
  return handle<CatalogoSugestao>(res);
}

export async function fetchCatalogoHistory(codigoIbge?: string): Promise<CatalogoHistoryItem[]> {
  const qs = codigoIbge ? `?codigo_ibge=${encodeURIComponent(codigoIbge)}` : '';
  const res = await fetch(`/api/catalogo/history${qs}`, { headers: authHeaders() });
  return handle<CatalogoHistoryItem[]>(res);
}

export async function exportCatalogo(): Promise<CatalogoExport> {
  const res = await fetch('/api/catalogo/export', { headers: authHeaders() });
  return handle<CatalogoExport>(res);
}

export async function importCatalogo(
  municipios: Omit<Municipio, 'id'>[],
): Promise<CatalogoImportResult> {
  const res = await fetch('/api/catalogo/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ municipios }),
  });
  return handle<CatalogoImportResult>(res);
}

// ---------------------------------------------------------------------------
// Onboarding assistido (S10-T07, US-S10-007)
// ---------------------------------------------------------------------------
//   GET  /api/onboarding                          -> checklist da org ativa
//   PUT  /api/onboarding/municipios               -> salva selecao (codigos IBGE)
//   POST /api/onboarding/email/test-smtp          -> testa SMTP do org_config
//   POST /api/onboarding/steps/{step}/complete    -> marca passo concluido
//   POST /api/onboarding/complete                 -> finaliza onboarding

export type OnboardingStepKey = 'email' | 'municipios' | 'thresholds' | 'tour';

export interface OnboardingStepState {
  key: OnboardingStepKey;
  status: 'pending' | 'completed';
  completed_at: string | null;
  /** true quando o passo vale pela configuracao real (sem registro no wizard). */
  auto: boolean;
}

export interface OnboardingEmailState {
  imap_configured: boolean;
  smtp_configured: boolean;
  configured: boolean;
}

export interface OnboardingState {
  org_id: string;
  status: 'in_progress' | 'completed';
  completed_at: string | null;
  progress: { done: number; total: number; percent: number };
  steps: OnboardingStepState[];
  municipios: string[];
  email: OnboardingEmailState;
  parceiros_notified?: number;
}

export async function fetchOnboarding(): Promise<OnboardingState> {
  const res = await fetch('/api/onboarding', { headers: authHeaders() });
  return handle<OnboardingState>(res);
}

export async function updateOnboardingMunicipios(
  codigoIbge: string[],
): Promise<OnboardingState> {
  const res = await fetch('/api/onboarding/municipios', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ codigo_ibge: codigoIbge }),
  });
  return handle<OnboardingState>(res);
}

export async function testOnboardingSmtp(): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  const res = await fetch('/api/onboarding/email/test-smtp', {
    method: 'POST',
    headers: authHeaders(),
  });
  return handle<{ success: boolean; message?: string; error?: string }>(res);
}

export async function completeOnboardingStep(
  step: OnboardingStepKey,
): Promise<OnboardingState> {
  const res = await fetch(`/api/onboarding/steps/${step}/complete`, {
    method: 'POST',
    headers: authHeaders(),
  });
  return handle<OnboardingState>(res);
}

export async function completeOnboarding(): Promise<OnboardingState> {
  const res = await fetch('/api/onboarding/complete', {
    method: 'POST',
    headers: authHeaders(),
  });
  return handle<OnboardingState>(res);
}
